import type { MemberAmounts, TierBreakdown, WithheldTiers } from '@/types/waterfall'
import { MEMBERS } from '@/types/waterfall'

export type EditableTierBreakdown = TierBreakdown

export function cloneTierBreakdown(row: TierBreakdown): EditableTierBreakdown {
  const cloneMember = (m: MemberAmounts): MemberAmounts => ({ ...m })
  return {
    t1: cloneMember(row.t1),
    hp: cloneMember(row.hp),
    s3a: cloneMember(row.s3a),
    s3b: cloneMember(row.s3b),
    s4a: cloneMember(row.s4a),
    s4b: cloneMember(row.s4b),
  }
}

export function tierKeys(): (keyof TierBreakdown)[] {
  return ['t1', 'hp', 's3a', 's3b', 's4a', 's4b']
}

export function tierColumnTotal(tier: MemberAmounts): number {
  return MEMBERS.reduce((sum, m) => sum + tier[m], 0)
}

export function gridGrandTotal(row: TierBreakdown): number {
  return tierKeys().reduce((sum, key) => sum + tierColumnTotal(row[key]), 0)
}

export function memberTotalsFromGrid(row: TierBreakdown): MemberAmounts {
  const totals = { JR: 0, KB: 0, LVW: 0, TR: 0, SM: 0 }
  for (const key of tierKeys()) {
    for (const m of MEMBERS) {
      totals[m] += row[key][m]
    }
  }
  return totals
}

export function parseCellInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return 0
  const normalized = trimmed.replace(/[$,\s]/g, '')
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) return null
  const parsed = Number(normalized)
  if (!Number.isFinite(parsed) || parsed < 0) return null
  return parsed
}

export function gridHasNegative(row: TierBreakdown): boolean {
  for (const key of tierKeys()) {
    for (const m of MEMBERS) {
      if (row[key][m] < 0) return true
    }
  }
  return false
}

const TIER_REDUCE_ORDER: (keyof TierBreakdown)[] = ['s4b', 's4a', 's3b', 's3a', 'hp', 't1']

export function memberWithheld(withheld: WithheldTiers, member: (typeof MEMBERS)[number]): number {
  return R2(withheld.s3b[member] + withheld.s4b[member])
}

export function gridCashTotals(
  grid: TierBreakdown,
  withheld: WithheldTiers,
): MemberAmounts {
  const gross = memberTotalsFromGrid(grid)
  const out = { ...gross }
  for (const m of MEMBERS) {
    out[m] = R2(gross[m] - memberWithheld(withheld, m))
  }
  return out
}

export function gridCashTotal(grid: TierBreakdown, withheld: WithheldTiers): number {
  const totals = gridCashTotals(grid, withheld)
  return R2(MEMBERS.reduce((sum, m) => sum + totals[m], 0))
}

export function allocateMemberTotals(
  proposal: TierBreakdown,
  actualTotals: MemberAmounts,
  lockedMembers?: Set<(typeof MEMBERS)[number]>,
): TierBreakdown {
  const grid = cloneTierBreakdown(proposal)
  const proposedTotals = memberTotalsFromGrid(proposal)

  for (const member of MEMBERS) {
    if (lockedMembers?.has(member)) continue
    const diff = R2(actualTotals[member] - proposedTotals[member])
    if (Math.abs(diff) < 0.005) continue

    if (diff < 0) {
      let remaining = Math.abs(diff)
      for (const tierKey of TIER_REDUCE_ORDER) {
        if (remaining <= 0) break
        const current = grid[tierKey][member]
        const cut = Math.min(current, remaining)
        grid[tierKey][member] = R2(current - cut)
        remaining = R2(remaining - cut)
      }
    } else {
      let added = false
      for (const tierKey of [...TIER_REDUCE_ORDER].reverse()) {
        if (proposal[tierKey][member] > 0) {
          grid[tierKey][member] = R2(grid[tierKey][member] + diff)
          added = true
          break
        }
      }
      if (!added) {
        const fallback =
          TIER_REDUCE_ORDER.find((tierKey) => tierColumnTotal(proposal[tierKey]) > 0) ?? 't1'
        grid[fallback][member] = R2(grid[fallback][member] + diff)
      }
    }
  }
  return grid
}

export function R2(x: number): number {
  return Math.round(x * 100) / 100
}

export function roundTierBreakdown(row: TierBreakdown): TierBreakdown {
  const roundMember = (m: MemberAmounts): MemberAmounts => {
    const out = { ...m }
    for (const member of MEMBERS) {
      out[member] = R2(out[member])
    }
    return out
  }
  const rounded = cloneTierBreakdown(row)
  for (const key of tierKeys()) {
    rounded[key] = roundMember(rounded[key])
  }
  return rounded
}

export function memberPaymentDelta(
  proposed: MemberAmounts,
  actual: MemberAmounts,
): Record<(typeof MEMBERS)[number], number> {
  const out = { JR: 0, KB: 0, LVW: 0, TR: 0, SM: 0 }
  for (const m of MEMBERS) {
    out[m] = R2(actual[m] - proposed[m])
  }
  return out
}

export function cellChanged(
  edited: TierBreakdown,
  proposal: TierBreakdown,
  tier: keyof TierBreakdown,
  member: (typeof MEMBERS)[number],
): boolean {
  return Math.abs(edited[tier][member] - proposal[tier][member]) > 0.001
}
